// src/cockpit4/components/GoalBoardPanel.tsx
// ============================================
// 经营中心 — 今日目标完成度看板（v5.1 补充细节二）
//
// 数据源：Obsidian 10_Operations（运营层）/Daily/{date}.md
// 原则：Rule 02 — 无真实数据显示"暂无数据"占位，不回退到 Mock
// ============================================

import { useState, useEffect, useCallback } from "react";
import {
  Target, CheckCircle2, XCircle, TrendingUp,
  Loader2, AlertCircle, ChevronRight, ChevronDown, Clock,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

interface DailyGoal {
  label: string;
  target: number;
  completed: number;
  unit: string;
}

interface HermesSuggestion {
  text: string;
  action: string;
}

interface GoalData {
  date: string;
  goals: DailyGoal[];
  overallProgress: number;  // 0-1
  hermesSuggestions: HermesSuggestion[];
  source: "obsidian" | "none";
  filePath?: string;
}

interface TrendPoint {
  date: string;
  progress: number;
}

// ============================================
// 解析 Daily 文件
// ============================================

function parseGoalFromDaily(content: string, date: string): GoalData {
  const goals: DailyGoal[] = [];

  const lines = content.split("\n");
  let currentSection = "";
  let suggestionText = "";
  let inFrontmatter = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // 跳过 frontmatter
    if (trimmed === "---") {
      inFrontmatter = !inFrontmatter;
      continue;
    }
    if (inFrontmatter) continue;

    // 跳过空行
    if (!trimmed) continue;

    // 检测段落标题
    if (/^##\s/.test(trimmed)) {
      currentSection = trimmed.replace(/^##\s+/, "");
      continue;
    }

    // 核心目标段落（匹配 - [x] **KPI名** 描述 格式）
    if (currentSection.includes("目标")) {
      const goalMatch = trimmed.match(/^-\s*\[([x\s])\]\s*\*\*(.+?)\*\*\s*(.*)$/);
      if (goalMatch) {
        const completed = goalMatch[1] === "x" ? 1 : 0;
        const label = goalMatch[2].trim();
        const existing = goals.find((g) => g.label === label);
        if (existing) {
          existing.completed += completed;
        } else {
          goals.push({ label, target: 1, completed, unit: "项" });
        }
        continue;
      }
    }

    // 任务清单段落（匹配 - [x] 07:00 描述 格式）
    if (currentSection.includes("任务")) {
      const taskMatch = trimmed.match(
        /^-\s*\[([x\s])\]\s*(?:(\d{2}:\d{2}))?\s*(.+)$/
      );
      if (taskMatch) {
        const completed = taskMatch[1] === "x";
        // 清理名称：去掉粗体和子进度括号
        const name = taskMatch[3]
          .replace(/\*\*/g, "")
          .replace(/\((\d+)\/(\d+)\)$/, "")
          .trim()
          .replace(/[🎯📝📊🔔💰⏰]/g, "")
          .trim();
        const current = completed ? 1 : 0;
        const existing = goals.find((g) => g.label === name);
        if (existing) {
          existing.completed += current;
          existing.target += 1;
        } else {
          goals.push({ label: name, target: 1, completed: current, unit: "项" });
        }
        continue;
      }
    }

    // 建议段落
    if (currentSection.includes("建议") && trimmed.startsWith("- ")) {
      suggestionText += trimmed + "\n";
    }
  }

  // 计算总进度
  const totalTarget = goals.reduce((s, g) => s + g.target, 0);
  const totalCompleted = goals.reduce((s, g) => s + Math.min(g.completed, g.target), 0);
  const overallProgress = totalTarget > 0 ? totalCompleted / totalTarget : 0;

  // 解析 Hermes 建议
  const suggestions: HermesSuggestion[] = suggestionText
    .split("\n")
    .filter((l) => l.match(/^[-*]\s/) && l.length > 3)
    .map((l) => ({
      text: l.replace(/^[-*]\s+/, "").trim(),
      action: "执行",
    }));

  return {
    date,
    goals,
    overallProgress,
    hermesSuggestions: suggestions,
    source: "obsidian",
    filePath: `10_Operations（运营层）/Daily/${date}.md`,
  };
}

// ============================================
// 获取近 7 天的趋势数据
// ============================================

function getLast7Days(): string[] {
  const days: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

// ============================================
// 主组件
// ============================================

export default function GoalBoardPanel({ compact = false }: { compact?: boolean }) {
  const [goalData, setGoalData] = useState<GoalData | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // compact 模式下默认折叠，点击展开/收起
  const [expanded, setExpanded] = useState(!compact);

  // 加载今日目标
  const loadTodayGoal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);

      // 使用全角括号匹配真实路径
      const respFull = await fetch(
        `/vault-api/read?path=${encodeURIComponent(
          `10_Operations（运营层）/Daily/${today}.md`
        )}`
      );

      // 也尝试半角括号
      const respHalf = respFull.ok
        ? respFull
        : await fetch(
            `/vault-api/read?path=${encodeURIComponent(
              `10_Operations/Daily/${today}.md`
            )}`
          );

      if (respHalf.ok) {
        const result = await respHalf.json();
        if (result.success && result.content) {
          const parsed = parseGoalFromDaily(result.content, today);
          setGoalData(parsed);
        } else {
          setGoalData({
            date: today,
            goals: [],
            overallProgress: 0,
            hermesSuggestions: [],
            source: "none",
          });
        }
      } else {
        setGoalData({
          date: today,
          goals: [],
          overallProgress: 0,
          hermesSuggestions: [],
          source: "none",
        });
      }
    } catch (e) {
      setError((e as Error).message || "加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载 7 天趋势
  const loadTrend = useCallback(async () => {
    const days = getLast7Days();
    const points: TrendPoint[] = [];

    for (const day of days) {
      try {
        const resp = await fetch(
          `/vault-api/read?path=${encodeURIComponent(
            `10_Operations（运营层）/Daily/${day}.md`
          )}`
        );
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && result.content) {
            const parsed = parseGoalFromDaily(result.content, day);
            points.push({ date: day.slice(5), progress: parsed.overallProgress });
            continue;
          }
        }
      } catch {
        // 跳过
      }
      points.push({ date: day.slice(5), progress: -1 }); // -1 表示无数据
    }

    setTrend(points);
  }, []);

  useEffect(() => {
    loadTodayGoal();
  }, [loadTodayGoal]);

  useEffect(() => {
    if (goalData) {
      loadTrend();
    }
  }, [goalData?.date, loadTrend]);

  // 手动刷新
  const handleRefresh = () => {
    loadTodayGoal();
  };

  // ============================================
  // 渲染
  // ============================================

  if (loading) {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-6 mb-3 flex items-center justify-center gap-2 text-gray-400 text-sm">
        <Loader2 size={14} className="animate-spin" />
        加载今日目标...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-500/15 bg-rose-500/[0.04] backdrop-blur-xl p-4 mb-3 flex items-center gap-2 text-rose-300 text-sm">
        <AlertCircle size={14} />
        {error}
        <button
          onClick={handleRefresh}
          className="ml-auto text-[11px] text-rose-400 hover:text-rose-200 underline"
        >
          重试
        </button>
      </div>
    );
  }

  // 无数据：Rule 02 — 不回退到 Mock
  if (!goalData || goalData.source === "none" || goalData.goals.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-amber-500/15 bg-amber-500/[0.03] backdrop-blur-xl p-6 mb-3">
        <div className="flex items-start gap-3">
          <Target size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-amber-300 mb-1">
              今日目标看板 — 暂无数据
            </h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              请在 Obsidian 中创建
              <code className="px-1 py-0.5 rounded bg-white/[0.04] text-gray-400 text-[11px] font-mono ml-1 mr-1">
                10_Operations（运营层）/Daily/{goalData?.date ?? "YYYY-MM-DD"}.md
              </code>
              文件，添加今日目标与任务清单。系统将自动读取并展示完成进度。
            </p>
            <button
              onClick={handleRefresh}
              className="mt-2 text-[11px] text-amber-400 hover:text-amber-200 underline transition"
            >
              刷新检查
            </button>
          </div>
        </div>
      </div>
    );
  }

  const progressPct = Math.round(goalData.overallProgress * 100);
  const hasHermesSuggestions = goalData.hermesSuggestions.length > 0;
  const incompleteGoals = goalData.goals.filter(
    (g) => g.completed < g.target
  );

  // compact 折叠态：单条进度条，不遮挡看板主区
  if (compact && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.04] transition-colors group flex-shrink-0"
        title="展开今日目标详情"
      >
        <Target size={13} className="text-cyan-400 flex-shrink-0" />
        <span className="text-[12px] font-medium text-gray-200 flex-shrink-0">今日目标看板</span>
        <span className="text-[10px] font-mono text-gray-600 flex-shrink-0">{goalData.date}</span>
        {/* 总进度条 */}
        <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden max-w-[240px]">
          <div
            className={`h-full rounded-full transition-all ${
              progressPct >= 70 ? "bg-green-500" : progressPct >= 40 ? "bg-amber-500" : "bg-red-500"
            }`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">{progressPct}%</span>
        <span className="text-[10px] text-gray-500 flex-shrink-0 hidden md:inline">
          {incompleteGoals.length > 0 ? `${incompleteGoals.length} 项待推进` : "目标全部完成"}
        </span>
        <ChevronDown size={11} className="text-gray-500 group-hover:text-gray-300 transition-colors flex-shrink-0" />
      </button>
    );
  }

  // 自动生成 Hermes 建议（当目标未完成时）
  const autoSuggestions: HermesSuggestion[] = incompleteGoals.length > 0 && !hasHermesSuggestions
    ? incompleteGoals.map((g) => ({
        text: `「${g.label}」完成度 ${Math.round((g.completed / g.target) * 100)}%，建议优先推进`,
        action: "执行",
      }))
    : [];

  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-4 mb-3">
      {/* ============ 标题栏 ============ */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target size={15} className="text-cyan-400" />
          <span className="text-xs font-medium text-gray-300">今日目标看板</span>
          <span className="text-[10px] text-gray-600">{goalData.date}</span>
          {goalData.source === "obsidian" && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/20">
              Obsidian
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* 进度圆环 */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-500">完成度</span>
            <div className="relative w-7 h-7">
              <svg viewBox="0 0 36 36" className="w-7 h-7 -rotate-90">
                <circle
                  cx="18" cy="18" r="15"
                  fill="none"
                  stroke="white"
                  strokeOpacity="0.06"
                  strokeWidth="3"
                />
                <circle
                  cx="18" cy="18" r="15"
                  fill="none"
                  stroke={progressPct >= 70 ? "#22C55E" : progressPct >= 40 ? "#F59E0B" : "#EF4444"}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${progressPct * 0.942} 94.2`}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-gray-200">
                {progressPct}
              </span>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="text-[10px] text-gray-500 hover:text-gray-300 transition"
            title="刷新"
          >
            <Loader2 size={11} className={loading ? "animate-spin" : ""} />
          </button>
          {compact && (
            <button
              onClick={() => setExpanded(false)}
              className="text-[10px] text-gray-500 hover:text-gray-300 transition flex items-center gap-1"
              title="收起"
            >
              <ChevronDown size={11} className="rotate-180" />
            </button>
          )}
        </div>
      </div>

      {/* ============ 目标列表 ============ */}
      <div className="space-y-1.5 mb-3">
        {goalData.goals.map((goal, i) => {
          const goalPct = goal.target > 0
            ? Math.round((goal.completed / goal.target) * 100)
            : 0;
          const isComplete = goal.completed >= goal.target;
          const isAtRisk = !isComplete && goalPct < 50;

          return (
            <div
              key={i}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition
                ${isComplete
                  ? "bg-green-500/[0.04] border border-green-500/10"
                  : isAtRisk
                    ? "bg-red-500/[0.05] border border-red-500/10"
                    : "bg-white/[0.02] border border-transparent"
                }`}
            >
              {isComplete ? (
                <CheckCircle2 size={12} className="text-green-400 flex-shrink-0" />
              ) : (
                <Clock size={12} className={isAtRisk ? "text-red-400" : "text-gray-500"} />
              )}
              <span className="text-[11px] text-gray-300 min-w-0 truncate flex-1">
                {goal.label}
              </span>
              <span className={`text-[10px] font-mono flex-shrink-0 ${
                isComplete ? "text-green-400" : isAtRisk ? "text-red-400" : "text-gray-500"
              }`}>
                {goal.completed}/{goal.target} {goal.unit}
              </span>
              {/* 进度条 */}
              <div className="w-12 h-1 rounded-full bg-white/[0.06] overflow-hidden flex-shrink-0">
                <div
                  className={`h-full rounded-full transition-all ${
                    isComplete ? "bg-green-500" : isAtRisk ? "bg-red-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${Math.min(goalPct, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ============ 底部：Hermes 建议 + 趋势 ============ */}
      <div className="flex gap-3">
        {/* Hermes 自动建议 */}
        <div className={`flex-1 rounded-lg p-2.5 border ${
          hasHermesSuggestions
            ? "border-violet-500/15 bg-violet-500/[0.04]"
            : "border-dashed border-white/[0.04] bg-white/[0.01]"
        }`}>
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp size={11} className="text-violet-400" />
            <span className="text-[10px] text-gray-500 font-medium">Hermes 建议</span>
          </div>
          {hasHermesSuggestions || autoSuggestions.length > 0 ? (
            <div className="space-y-1">
              {(goalData.hermesSuggestions.length > 0
                ? goalData.hermesSuggestions
                : autoSuggestions
              ).slice(0, 3).map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-200 cursor-pointer transition group"
                  title="点击可跳转执行"
                >
                  <ChevronRight size={9} className="text-violet-500 flex-shrink-0" />
                  <span className="truncate">{s.text}</span>
                  <span className="text-[9px] text-violet-500 opacity-0 group-hover:opacity-100 transition ml-auto flex-shrink-0">
                    {s.action}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-gray-600">目标全部完成 🎯</p>
          )}
        </div>

        {/* 7 天趋势 */}
        <div className="w-36 flex-shrink-0 rounded-lg border border-white/[0.06] bg-white/[0.01] p-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <TrendingUp size={11} className="text-cyan-400" />
            <span className="text-[10px] text-gray-500">近7天趋势</span>
          </div>
          <div className="flex items-end gap-1 h-10">
            {trend.map((p, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                <div
                  className="w-full rounded-t transition-all"
                  style={{
                    height: p.progress >= 0 ? `${Math.max(p.progress * 36, 2)}px` : "2px",
                    backgroundColor:
                      p.progress < 0
                        ? "rgba(255,255,255,0.04)"
                        : p.progress >= 0.7
                          ? "#22C55E"
                          : p.progress >= 0.4
                            ? "#F59E0B"
                            : "#EF4444",
                    opacity: p.progress < 0 ? 0.3 : 1,
                  }}
                />
                <span className="text-[8px] text-gray-600">{p.date}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-1">
            {trend.filter((p) => p.progress >= 0).length > 0 ? (
              <span className="text-[9px] text-gray-600">
                均{" "}
                {Math.round(
                  (trend
                    .filter((p) => p.progress >= 0)
                    .reduce((s, p) => s + p.progress, 0) /
                    Math.max(trend.filter((p) => p.progress >= 0).length, 1)) *
                    100
                )}
                %
              </span>
            ) : (
              <span className="text-[9px] text-gray-700">无历史</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
